import { useMemo } from "react";
import { Form } from "react-bootstrap";
import { Leaf, Sprout } from "lucide-react";

import TableMultiple from "@/components/Table/Multiple/TableMultiple";
import CustomBadge from "@/components/desktop/badge/CustomBadge";
import OATooltip from "../Tooltip/OATooltip";
import ValorizationTooltip from "../Tooltip/ValorizationTooltip.jsx";

import { dateToLocale, formatMilesDecimalNew } from "@/services/Utils";
import { formatNumber } from "@/utils/numberFormat";
import { normalizePercent } from "@/pages/Planning/utils/normalizePercent";
import { getApplicationOrderStateVariant } from "@/constants/applicationOrderStates";

import styles from "./useOrderApplicationColumns.module.scss";

import AppOrdersDetail from "../Detail/OrderApplicationDetail";
import AppOrdersActionItem from "../Actions/OrderApplicationActionItem";

export function useOrderApplicationColumns({ customer, data, setData, base, selectedRef, toggleSelect, handleShow, canShowPrice = false }) {
	const sizeProducts = customer?.use_code_oa ? 45 : 50;

	const columns = useMemo(() => {
		let cols = [
			{
				Header: "",
				size: "4",
				accessor: "id",
				order: true,
				Cell: ({ row }) => {
					return (
						<Form.Check
							type="checkbox"
							checked={selectedRef.current.has(Number(row.original.id))}
							onChange={() => toggleSelect(Number(row.original.id))}
						/>
					);
				},
			},
			{
				Header: "N° OA",
				size: "5",
				accessor: "code",
				order: true,
				style: { flexDirection: "row-reverse" },
				Cell: ({ row }) => <OATooltip row={row} totalRow={data?.length || 0} />,
			},
			{
				Header: "Campo",
				size: "8",
				accessor: "estate_parent_name",
				order: true,
				Cell: ({ row }) => {
					const { original: item } = row ?? {};
					return item.estate_parent_name;
				},
			},
			{
				Header: "Estado",
				size: "3",
				accessor: "application_orders_state_id",
				order: true,
				Cell: ({ row }) => {
					const { original: item, index: rowIndex } = row ?? {};
					const percentUsed = normalizePercent(item?.tooltip?.head?.percent_used);
					const stateBadge = (
						<CustomBadge variant={getApplicationOrderStateVariant(Number(item.application_orders_state_id))} size="md">
							{item.application_orders_state_name}
						</CustomBadge>
					);
					return (
						<div className={styles.stateCell}>
							{Number(item.application_orders_state_id) === 1 ? (
								<AppOrdersActionItem
									item={item}
									indice={rowIndex}
									data={data}
									setData={setData}
									base={base}
									approveRejectOnly={true}
									approveRejectOnlyComponent={stateBadge}
								/>
							) : (
								stateBadge
							)}
							<div className={styles.oaUsage}>
								<span className={styles.oaUsageLabel}>Utilizado</span>
								<span className={`${styles.oaUsageValue} ${percentUsed === 100 ? styles.oaUsageValueComplete : ""}`}>
									{formatNumber(percentUsed)}%
								</span>
							</div>
						</div>
					);
				},
			},
			{
				Header: "Fecha",
				size: "7",
				accessor: "application_order_date",
				order: true,
				Cell: ({ row }) => <>{dateToLocale(row.values.application_order_date)}</>,
			},
			{
				Header: "Centro Costo",
				size: "10",
				accessor: "business_center_names",
				order: true,
				isMultiple: true,
				Cell: ({ row }) => {
					const { original: item } = row ?? {};

					return (
						<div className="flex flex-col gap-4">
							<div>
								<TableMultiple textItems={item?.business_center_names} showFunction={handleShow} showTitle={null} />
							</div>

							<div>
								<div className="flex items-center gap-1 text-sm font-semibold text-gray-700">
									<Leaf size={16} className="text-green-600" />
									<strong style={{ marginLeft: "3px", textDecoration: "underline" }}>Especie</strong>
								</div>
								<TableMultiple textItems={item?.specie} showFunction={handleShow} showTitle={null} />
							</div>

							<div>
								<div className="flex items-center gap-1 text-sm font-semibold text-gray-700">
									<Sprout size={16} className="text-emerald-600" />
									<strong style={{ marginLeft: "3px", textDecoration: "underline" }}>Variedad</strong>
								</div>
								<TableMultiple textItems={item?.variety} showFunction={handleShow} showTitle={null} />
							</div>
						</div>
					);
				},
			},
			{
				Header: "Total Ha",
				size: "6",
				accessor: "areas_sum",
				order: true,
				Cell: ({ row }) => {
					return formatMilesDecimalNew(row.values.areas_sum);
				},
			},
			{
				Header: "Mojamiento (L/ha)",
				size: "5",
				accessor: "application_orders_machineries_wetness",
				order: false,
				Cell: ({ row }) => {
					return formatMilesDecimalNew(row.values.application_orders_machineries_wetness);
				},
			},
			{
				Header: "Productos",
				size: sizeProducts,
				accessor: "",
				order: true,
				Cell: ({ row }) => {
					const { original: item, index: i } = row ?? {};

					return (
						<div
							style={{
								width: "100%",
								display: "flex",
								flexDirection: "column",
							}}
						>
							<AppOrdersDetail indice={i} application_order={item} isCopy={false} />
							{!!item.calculated_machined && <AppOrdersDetail indice={i} application_order={item} isCopy={true} />}
						</div>
					);
				},
			},
			{
				Header: "Acción",
				size: "2",
				accessor: "",
				Cell: ({ row }) => {
					const { original: item, index: i } = row ?? {};
					return <AppOrdersActionItem item={item} indice={i} data={data} setData={setData} base={base} />;
				},
			},
		];

		if (canShowPrice) {
			const columnPrice = {
				Header: "$ Valor",
				size: "10",
				accessor: "total",
				order: false,
				Cell: ({ row }) => <ValorizationTooltip row={row} />,
			};

			const index = cols.findIndex((col) => col.accessor === "application_orders_machineries_wetness");
			if (index !== -1) {
				cols.splice(index + 1, 0, columnPrice);
			}
		}

		if (customer?.use_code_oa) {
			const newColumn = {
				Header: "Cód. Interno",
				size: "5",
				accessor: "internal_code",
				order: true,
				Cell: ({ row }) => <div style={{ paddingLeft: "5px" }}>{row.original.internal_code || "-"}</div>,
			};

			const index = cols.findIndex((col) => col.accessor === "code");

			if (index !== -1) {
				cols.splice(index + 1, 0, newColumn);
			}
		}

		if (customer.use_sectors) {
			cols = cols.map((column) => {
				if (column.accessor === "business_center_names") {
					return {
						...column,
						Header: "Sectores",
						Cell: ({ row }) => {
							const { original: item } = row ? row : {};
							return <TableMultiple textItems={item?.business_center_names} showFunction={handleShow} showTitle={"Sectores"} />;
						},
					};
				}
				return column;
			});

			const newColumn = {
				Header: "Centro de Costo",
				size: "10",
				accessor: "business_center_parents_name",
				order: true,
				isMultiple: true,
				Cell: ({ row }) => {
					const { original: item } = row ? row : {};
					return <TableMultiple textItems={item?.business_center_parents_name} showFunction={handleShow} showTitle={"Centro de Costo"} />;
				},
			};

			cols = cols.reduce((acc, column) => {
				acc.push(column);
				if (column.accessor === "variety") {
					acc.push(newColumn);
				}
				return acc;
			}, []);
		}

		return cols;
		// eslint-disable-next-line
	}, [data, customer, base, selectedRef]);

	return columns;
}
